import React, { ReactElement, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Event } from 'services/api';
import { Image } from 'components/lib';
import {
  COLORS,
  FONT,
  MaterialSymbol,
  StatusChip,
  YellowCtaButton,
  SkeletonBlock,
  formatLongDate,
  formatTimeRange,
  isHappeningNow,
} from '../shared';

export interface CompactEventCardProps {
  event: Event;
  checkedIn: boolean;

  onCheckIn: () => void;
  checkInLabel?: string;

  onDetails?: () => void;
  showDetails?: boolean;

  onEventClick?: () => void;

  ctaDisabled?: boolean;
  ctaLoading?: boolean;
}

// Figma 4ZdHY8bgfpcTL4REWmZ7sO / 8081:6355 — flyer width; Details CTA matches it.
const IMAGE_WIDTH = 100;
const IMAGE_HEIGHT = 133;
// Figma card is 400px; never grow wider — wrap instead.
export const COMPACT_EVENT_CARD_WIDTH = 400;

export const CompactEventCard = ({
  event,
  checkedIn,
  onCheckIn,
  checkInLabel = 'Check in',
  onDetails,
  showDetails = false,
  onEventClick,
  ctaDisabled = false,
  ctaLoading = false,
}: CompactEventCardProps): ReactElement => {
  const [imgError, setImgError] = useState(false);
  const live = isHappeningNow(event);
  const showImage = event.flyerImageUrl && !imgError;
  const handleEventClick = onEventClick ?? onDetails;

  return (
    <Box
      sx={{
        backgroundColor: COLORS.white,
        border: `1px solid ${COLORS.cardBorder}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        p: '16px',
        width: '100%',
        maxWidth: `${COMPACT_EVENT_CARD_WIDTH}px`,
        minWidth: 0,
        overflow: 'hidden',
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(20,31,51,0.10)' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <ButtonBase
          onClick={handleEventClick}
          disableRipple
          aria-label={`View details for ${event.name}`}
          sx={{
            width: `${IMAGE_WIDTH}px`,
            height: `${IMAGE_HEIGHT}px`,
            flexShrink: 0,
            backgroundColor: COLORS.imagePlaceholder,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            '&:focus-visible': {
              outline: `2px solid ${COLORS.navy}`,
              outlineOffset: '-2px',
            },
          }}
        >
          {showImage ? (
            <Image
              src={event.flyerImageUrl ?? ''}
              alt={event.name}
              width={IMAGE_WIDTH}
              height={IMAGE_HEIGHT}
              onError={() => setImgError(true)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <MaterialSymbol name="image" size={28} color={COLORS.muted} />
          )}
        </ButtonBase>

        <Box
          sx={{
            flex: '1 0 0',
            minWidth: 0,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            p: '16px',
            overflow: 'hidden',
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap="16px"
            width="100%"
            minWidth={0}
          >
            <Box
              sx={{
                width: '100%',
                height: '38px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                overflow: 'hidden',
              }}
            >
              <Typography
                component={ButtonBase}
                onClick={handleEventClick}
                disableRipple
                sx={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: '15px',
                  lineHeight: '19px',
                  color: COLORS.headingDark,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  textAlign: 'left',
                  width: '100%',
                  '&:hover': { textDecoration: 'underline' },
                  '&:focus-visible': {
                    outline: `2px solid ${COLORS.navy}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                {event.name}
              </Typography>
            </Box>

            <Box display="flex" flexDirection="column" gap="4px" width="100%">
              {live ? (
                <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}>
                  <StatusChip />
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '24px',
                    color: COLORS.bodyDark,
                  }}
                >
                  {formatLongDate(event.date)}
                </Typography>
              )}
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '24px',
                  color: COLORS.bodyDark,
                }}
              >
                {formatTimeRange(event.startTime, event.endTime)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        display="flex"
        gap="16px"
        alignItems="center"
        width="100%"
        sx={{ minWidth: 0 }}
      >
        {showDetails && (
          <ButtonBase
            onClick={onDetails}
            disableRipple
            sx={{
              width: `${IMAGE_WIDTH}px`,
              height: '45px',
              flexShrink: 0,
              px: '20px',
              py: '12px',
              borderRadius: '8px',
              border: `1px solid ${COLORS.cardBorder}`,
              backgroundColor: COLORS.white,
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: '14px',
              color: COLORS.headingDark,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: COLORS.hoverRow },
              '&:focus-visible': {
                outline: `2px solid ${COLORS.bodyDark}`,
                outlineOffset: '2px',
              },
            }}
          >
            Details
          </ButtonBase>
        )}

        {checkedIn ? (
          <Box
            sx={{
              backgroundColor: COLORS.skeleton,
              display: 'flex',
              gap: '6px',
              height: '45px',
              alignItems: 'center',
              justifyContent: 'center',
              px: '14px',
              py: '10px',
              borderRadius: '8px',
              flex: '1 0 0',
              minWidth: 0,
            }}
          >
            <MaterialSymbol
              name="check"
              size={18}
              color={COLORS.headingDark}
              fill={1}
            />
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 500,
                fontSize: '14px',
                color: COLORS.headingDark,
                whiteSpace: 'nowrap',
              }}
            >
              Checked in
            </Typography>
          </Box>
        ) : (
          <YellowCtaButton
            fullWidth={!showDetails}
            height={45}
            onClick={onCheckIn}
            disabled={ctaDisabled}
            loading={ctaLoading}
            ariaLabel={`${checkInLabel} to ${event.name}`}
            sx={{
              flex: '1 0 0',
              minWidth: 0,
              width: showDetails ? 'auto' : '100%',
              px: '14px',
              py: '10px',
            }}
          >
            <MaterialSymbol
              name="qr_code_scanner"
              size={18}
              color={
                ctaDisabled ? COLORS.ctaDisabledText : COLORS.headingDark
              }
            />
            {ctaLoading ? 'Checking in…' : checkInLabel}
          </YellowCtaButton>
        )}
      </Box>
    </Box>
  );
};

export const CompactEventCardSkeleton = (): ReactElement => (
  <Box
    sx={{
      backgroundColor: COLORS.white,
      border: `1px solid ${COLORS.cardBorder}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      p: '16px',
      width: '100%',
      maxWidth: `${COMPACT_EVENT_CARD_WIDTH}px`,
    }}
  >
    <Box display="flex" alignItems="center" width="100%">
      <SkeletonBlock width={IMAGE_WIDTH} height={IMAGE_HEIGHT} />
      <Box
        sx={{
          flex: '1 0 0',
          p: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <SkeletonBlock width="90%" height={38} />
        <Box display="flex" flexDirection="column" gap="4px">
          <SkeletonBlock width="70%" height={24} />
          <SkeletonBlock width="45%" height={24} />
        </Box>
      </Box>
    </Box>
    <Box display="flex" gap="16px" width="100%">
      <SkeletonBlock width={IMAGE_WIDTH} height={45} radius={8} />
      <SkeletonBlock width="100%" height={45} radius={8} />
    </Box>
  </Box>
);
